/**
 * @file firmware.ts
 * @description Telink 3228C 鼠标固件：应用态版本、云端检查更新、USB OTA。
 *
 * ## 分层
 * - 公开 API：`features/firmware/contract.ts` → `getFirmwareVersion` / `checkFirmwareUpdate` / `updateFirmware`
 * - 本文件：版本读写、云端拉取、进 Bootloader、按帧写 BIN
 * - AP 编解码：`protocols/vision-ap-mouse.ts`（`0xB0` / `0x93`）
 * - OTA 编解码：`protocols/mouse-usb-ota.ts`（进窗 `05 AA BB CC`、写 BIN 状态机）
 * - device：装配本 feature；提供 `runAtomic` / `session.channels`
 *
 * ## 升级目标
 * - **有线**：升级鼠标本体；应用态须 ≥ `1.06` 才能进 USB OTA
 * - **无线（插接收器）**：升级接收器；与鼠标共用同一套 USB OTA 状态机
 * - **不开放**：2.4G 空中刷鼠标；退出 Bootloader 的专用命令协议未给出（设备自行重启）
 *
 * ## updateFirmware() 时序
 * ```
 * ① usbOtaTargetType / checkFirmwareUpdate  判定本端是否可升、选中目标
 * ② getVersionInfo + downloadFirmware       拉 version.json 与 ZIP，取出唯一 .bin
 * ③ parseOpenBin                            解析为 Open BIN 布局
 * ④ enterUsbOta                             发进窗，成功认控制口 04 … FF
 * ⑤ delay 3000ms                            等 Bootloader 接口稳定
 * ⑥ writeUsbOtaFirmware                     TelinkKmUsbOtaHostSession 逐帧写；中间 Input 仅背压
 * ⑦ 结束帧须收到 status==0；设备随后自行重启
 * ```
 *
 * @see docs/telink-km-ota-upgrade-flow.md
 * @see docs/protocols/telink-km-ota/v5.0.2/protocol.md
 */

import type { HidChannel, HidReportListener } from '../../../../channels/hid/HidChannel'
import { InputReportExchange } from '../../../../channels/hid/exchange/InputReportExchange'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { FirmwareEvents } from '../../../../../src/events/FirmwareEvents'
import { ConnectionType } from '../../../../types/connection'
import {
  DeviceType,
  VersionType,
  type RemoteDeviceVersion,
  type VersionInfo,
} from '../../../../types/firmware'
import { delay } from '../../../../utils/TimeUtils'
import { compareVersions } from '../../../../utils/verify'
import { processZip } from '../../../shared/firmware/shared/FileUtil'
import {
  createVisionApPayload,
  isVisionApResponseForRequest,
  parseVisionApFirmwareVersion,
  parseVisionApResponse,
  VISION_AP_FIRMWARE_VERSION,
  VISION_AP_MOUSE_COMMAND,
  VISION_AP_MOUSE_HID,
} from '../../protocols/vision-ap-mouse'
import {
  createTelinkMouseEnterUsbOtaPayload,
  isTelinkMouseEnterUsbOtaSuccess,
  parseTelinkKmUsbOpenBin,
  parseTelinkKmUsbOtaFinalStatus,
  TELINK_MOUSE_USB_OTA_HID,
  TelinkKmUsbOtaHostSession,
} from '../../protocols/mouse-usb-ota'

/** 应用态版本命令正常等待时间。 */
const COMMAND_RESPONSE_TIMEOUT_MS = 3000
/** 写 BIN 时单帧等待 Bootloader Input 的超时（C# 以任意 Input 为背压）。 */
const OTA_FRAME_TIMEOUT_MS = 5000
/** 读版本首次探测超时；超时后会唤醒重试。 */
const WAKE_PROBE_TIMEOUT_MS = 500
const WAKE_RETRY_DELAY_MS = 100
const VERSION_FILE_NAME = 'version.json'
/** 进入 Bootloader 后给升级接口稳定时间；键盘路径同样使用 3000ms。 */
const OTA_BOOT_WAIT_MS = 3000
/** 维盛确认：低于该应用态版本无法完成进入 USB OTA。 */
const MIN_USB_OTA_APP_VERSION = '1.06'

type TelinkMouseFirmwareVersionFile = {
  Device?: RemoteDeviceVersion[]
}

/** 上报给 UI 的升级阶段（FirmwareEvents.Progress）。 */
type FirmwareUpdateStage = 'downloading' | 'downloaded' | 'bootloader' | 'writing' | 'written'

/**
 * VT2 ULTRA V2 固件 feature。
 * 有线升鼠标、无线升接收器；写 BIN 与进窗细节见文件头时序。
 */
export class Telink3228CMouseFirmware extends FirmwareFeature {
  /**
   * 读应用态固件版本（控制口）。
   * - 有线：仅 `0xB0` 鼠标版本
   * - 无线：再读 `0x93` 接收器版本
   * 返回的 `versionType` 统一为 USB（对应 USB OTA 固件包展示槽位）。
   */
  public async getFirmwareVersion(): Promise<VersionInfo[]> {
    const mouse = await this.readApFirmwareVersion(
      VISION_AP_MOUSE_COMMAND.readMouseVersion,
      '读取鼠标版本',
    )
    const versions: VersionInfo[] = [
      {
        version: mouse.text,
        // 该应用态版本对应 USB OTA 固件包；统一固件页以 USB 作为主固件展示槽位。
        versionType: VersionType.USB,
        deviceType: DeviceType.Mouse,
      },
    ]
    if (this.device.getConnectionType() !== ConnectionType.Wireless) {
      return versions
    }
    const dongle = await this.readApFirmwareVersion(
      VISION_AP_MOUSE_COMMAND.readDongleVersion,
      '读取接收器版本',
    )
    versions.push({
      version: dongle.text,
      versionType: VersionType.USB,
      deviceType: DeviceType.Receiver,
    })
    return versions
  }

  /**
   * 对比本地版本与云端 `version.json`，标记 `canUpdate` / `onlineVersion`。
   * 与 nRF54 系列一致：检查阶段只比较线上/本地版本，不按当前连接方式屏蔽槽位。
   * 升级阶段再根据连接方式选择鼠标本体（有线）或接收器（无线）作为实际目标。
   */
  public async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const versions = await this.getFirmwareVersion()
    const mouseRelease = await this.getVersionInfo(this.device.manifest.mainControl)
    const receiverRelease =
      this.device.getConnectionType() === ConnectionType.Wireless
        ? await this.getVersionInfo(this.device.manifest.dongleModel)
        : null
    return versions.map((version) => {
      const release = version.deviceType === DeviceType.Receiver ? receiverRelease : mouseRelease
      const onlineVersion = release?.FirmwareVersion
      const canUpdate =
        Boolean(onlineVersion) && this.canUpdateByVersion(onlineVersion, version.version)
      return {
        ...version,
        canUpdate,
        ...(onlineVersion ? { onlineVersion } : {}),
      }
    })
  }

  /**
   * 完整 USB OTA：检查 → 下载 → 进 Bootloader → 写 BIN。
   * 无可升级目标时直接 return（不抛错）。
   * @param _report 契约保留参数，本实现改用 `FirmwareEvents` 上报进度
   */
  public async updateFirmware(_report?: () => void): Promise<void> {
    const targetType = this.usbOtaTargetType()
    const versions = await this.checkFirmwareUpdate()
    const target = versions.find(
      (version) => version.deviceType === targetType && version.canUpdate,
    )
    if (!target) return

    if (
      targetType === DeviceType.Mouse &&
      compareVersions(target.version, MIN_USB_OTA_APP_VERSION) < 0
    ) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED_VERSION,
        `应用态 ${target.version} 低于 ${MIN_USB_OTA_APP_VERSION}，无法进入 USB OTA`,
      )
    }

    const model =
      targetType === DeviceType.Receiver
        ? this.device.manifest.dongleModel
        : this.device.manifest.mainControl
    const release = await this.getVersionInfo(model)
    if (!release?.FirmwareURL) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件下载地址为空')
    }

    this.reportStage(
      'downloading',
      `正在下载固件包（线上版本：${release.FirmwareVersion ?? '未提供'}）`,
    )
    const file = await this.downloadFirmware(model, release.FirmwareURL)
    const bin = this.parseOpenBin(file)
    this.reportStage(
      'downloaded',
      `固件包下载完成（版本：${release.FirmwareVersion ?? '未提供'}，文件：${release.FirmwareURL}，BIN：${bin.padded.byteLength} 字节）`,
    )

    this.reportStage('bootloader', '正在进入 USB Bootloader')
    await this.enterUsbOta()
    await delay(OTA_BOOT_WAIT_MS)

    this.reportStage('writing', 'Bootloader 已就绪，正在写入固件', 0)
    await this.writeUsbOtaFirmware(bin)
    this.reportStage('written', '固件写入完成，设备将自行重启', 100)
  }

  /**
   * 拉取 `{base}/{model}/version.json`，取 `Device[0]` 作为线上发布信息。
   * @returns 无 model 时返回 `null`（该槽位不检查更新）
   */
  private async getVersionInfo(model: string | undefined): Promise<RemoteDeviceVersion | null> {
    if (!model) return null
    // 不直接维护正式固件地址，先走测试地址
    // TODO：上线开放
    this.device.__setFirmwareBaseUrl('https://soft.rapoo.com/RapooDriver/FirmwareTest')
    const response = await fetch(
      `${this.device.getFirmwareBaseUrl()}/${encodeURIComponent(model)}/${VERSION_FILE_NAME}?t=${Date.now()}`,
      { referrerPolicy: 'no-referrer' },
    )
    if (!response.ok) {
      throw new DeviceError(
        DeviceErrorCode.TIMEOUT,
        `读取 ${model} 在线版本失败：${response.status}`,
      )
    }

    const data = (await response.json()) as TelinkMouseFirmwareVersionFile
    const release = data.Device?.[0]
    if (!release) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${model} 在线版本文件缺少 Device 配置`)
    }
    return release
  }

  /**
   * 下载固件 ZIP 并解出唯一 `.bin`。
   * URL：`{base}/{model}/{fileName}`。
   */
  private async downloadFirmware(model: string | undefined, fileName: string): Promise<Uint8Array> {
    if (!model || !fileName) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件型号或文件名为空')
    }

    const files = await processZip(
      `${this.device.getFirmwareBaseUrl()}/${encodeURIComponent(model)}/${encodeURIComponent(fileName)}`,
      { referrerPolicy: 'no-referrer' },
    )
    const entries = Object.values(files).filter(({ extension }) => extension === '.bin')
    if (entries.length !== 1 || !entries[0]) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Telink 固件包中缺少唯一 BIN 文件')
    }
    return entries[0].data
  }

  /**
   * 控制口发 AP 读版本命令（`0xB0` / `0x93`），校验 ACK/LEN/ADDR 后解析版本字符串。
   * 带一次短超时唤醒重试（休眠设备可能首包无响应）。
   */
  private async readApFirmwareVersion(
    command: number,
    action: string,
  ): Promise<ReturnType<typeof parseVisionApFirmwareVersion>> {
    return this.device.runAtomic(async (channel) => {
      const payload = createVisionApPayload(
        command,
        VISION_AP_FIRMWARE_VERSION.dataLength,
        VISION_AP_FIRMWARE_VERSION.address,
      )
      const responseView = await this.requestVersionResponse(channel, payload, action)
      const parsed = parseVisionApResponse(responseView, command)
      if (parsed.ack !== 0) {
        throw new DeviceError(
          DeviceErrorCode.READ_FAILED,
          `维盛 AP 命令 0x${command.toString(16)} 返回 ACK=0x${parsed.ack.toString(16).padStart(2, '0')}`,
        )
      }
      if (
        parsed.address !== VISION_AP_FIRMWARE_VERSION.address ||
        parsed.length !== VISION_AP_FIRMWARE_VERSION.dataLength
      ) {
        throw new DeviceError(DeviceErrorCode.READ_FAILED, `${action}响应的 LEN 或 ADDR 不匹配`)
      }
      return parseVisionApFirmwareVersion(parsed.data)
    })
  }

  /**
   * 读版本 exchange：先短超时探测；超时则 delay 后再用正常超时重试一次。
   * 第二次仍超时提示用户移动/点击鼠标唤醒。
   */
  private async requestVersionResponse(
    channel: HidChannel,
    payload: Uint8Array,
    action: string,
  ): Promise<DataView> {
    try {
      return await this.exchangeVersionInputReport(channel, payload, WAKE_PROBE_TIMEOUT_MS)
    } catch (error) {
      if (!(error instanceof DeviceError) || error.code !== DeviceErrorCode.TIMEOUT) {
        throw error
      }
    }

    await delay(WAKE_RETRY_DELAY_MS)
    try {
      return await this.exchangeVersionInputReport(channel, payload, COMMAND_RESPONSE_TIMEOUT_MS)
    } catch (retryError) {
      if (retryError instanceof DeviceError && retryError.code === DeviceErrorCode.TIMEOUT) {
        throw new DeviceError(
          DeviceErrorCode.TIMEOUT,
          `${action}在唤醒重试后仍未响应；请移动或点击鼠标后重试`,
          retryError,
        )
      }
      throw retryError
    }
  }

  /** 单次控制口 InputReportExchange：发版本请求并校验为对应回包。 */
  private async exchangeVersionInputReport(
    channel: HidChannel,
    payload: Uint8Array,
    timeout: number,
  ): Promise<DataView> {
    const exchange = new InputReportExchange(channel)
    return exchange.request({
      reportId: VISION_AP_MOUSE_HID.controlReportId,
      payload,
      responseReportId: VISION_AP_MOUSE_HID.controlReportId,
      timeout,
      validate: (input) => isVisionApResponseForRequest(input, payload),
    })
  }

  /**
   * 当前连接对应的 USB OTA 目标：无线→接收器，有线→鼠标。
   * （2.4G 刷鼠标不在此路径。）
   */
  private usbOtaTargetType(): DeviceType.Mouse | DeviceType.Receiver {
    return this.device.getConnectionType() === ConnectionType.Wireless
      ? DeviceType.Receiver
      : DeviceType.Mouse
  }

  /** 将下载到的 BIN 解析为 Open 布局；协议校验失败包装为 INVALID_PARAM。 */
  private parseOpenBin(file: Uint8Array) {
    try {
      return parseTelinkKmUsbOpenBin(file)
    } catch (error) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        error instanceof Error ? error.message : String(error),
      )
    }
  }

  /**
   * 进入 USB Bootloader。
   * 优先控制口发 `05 AA BB CC`，成功只认控制口 `04 … FF`；
   * 控制口 WRITE_FAILED 时才回退升级口再发一次。
   * 结束后释放 update 通道（若与主口共享 native 则只 unregister，避免关掉主通道）。
   */
  private async enterUsbOta(): Promise<void> {
    await this.device.runAtomic(async () => {
      const payload = createTelinkMouseEnterUsbOtaPayload()
      const update = await this.device.session.channels.openChannel({ usageType: 'update' })
      try {
        await this.sendEnterUsbOtaCommandOnChannels(payload, update)
      } finally {
        if (update) await this.releaseUsbOtaChannel(update)
      }
    })
  }

  /**
   * 在 Bootloader 态按 C# `ota_write` 状态机写完整 BIN。
   * - 优先走 `usageType: 'update'` 通道，否则回退主控制口
   * - 每帧发完等任意合法 Input 作背压；`status≠0` 立即失败
   * - 结束帧必须收到最终 `status==0`；若设备已断开则失败
   */
  private async writeUsbOtaFirmware(
    bin: ReturnType<typeof parseTelinkKmUsbOpenBin>,
  ): Promise<void> {
    await this.device.runAtomic(async () => {
      const update = await this.device.session.channels.openChannel({ usageType: 'update' })
      const sendChannel = update ?? this.device.channel
      try {
        const session = new TelinkKmUsbOtaHostSession(bin)
        for (;;) {
          const frame = session.next()
          if (!frame) break
          const response = await this.exchangeAnyUsbOtaInput(sendChannel, frame.payload)
          const status =
            response === 'disconnected' ? undefined : parseTelinkKmUsbOtaFinalStatus(response)
          this.reportProgress(frame.percent)
          if (status !== undefined && status !== 0) {
            throw new DeviceError(DeviceErrorCode.WRITE_FAILED, `OTA Fail=${status}`)
          }
          if (frame.phase === 'end') {
            if (response === 'disconnected') {
              throw new DeviceError(
                DeviceErrorCode.WRITE_FAILED,
                '结束帧后设备断开，未收到最终 status==0',
              )
            }
            if (status !== 0) {
              throw new DeviceError(
                DeviceErrorCode.WRITE_FAILED,
                '结束帧后未收到 05 02 03 00 06 FF 且 status==0',
              )
            }
          }
        }
      } finally {
        if (update) await this.releaseUsbOtaChannel(update)
      }
    })
  }

  /**
   * 进窗命令发送策略：先控制口发并多通道听成功包；
   * 若控制口发送失败且存在独立 update 通道，再改从 update 发送。
   * Report ID `05` 同内容回包与请求回显都不能当作成功。
   */
  private async sendEnterUsbOtaCommandOnChannels(
    payload: Uint8Array,
    update: HidChannel | null,
  ): Promise<void> {
    const listenChannels = [this.device.channel]
    if (update && update !== this.device.channel) listenChannels.push(update)

    try {
      await this.exchangeUsbOtaSuccessReport(listenChannels, this.device.channel, payload)
      return
    } catch (error) {
      if (
        !(error instanceof DeviceError) ||
        error.code !== DeviceErrorCode.WRITE_FAILED ||
        !update ||
        update === this.device.channel
      ) {
        throw error
      }
    }

    if (!update) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '设备未确认进入 USB 升级模式')
    }

    const fallback = await this.exchangeUsbOtaSuccessReport(listenChannels, update, payload)
    if (fallback !== 'disconnected' && !isTelinkMouseEnterUsbOtaSuccess(fallback)) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '设备未确认进入 USB 升级模式')
    }
  }

  /**
   * 发进窗命令并等待「进入升级模式成功」包（`04 AA BB CC … FF`）。
   * @returns 成功时的 DataView，或设备已断开时的 `'disconnected'`
   */
  private async exchangeUsbOtaSuccessReport(
    listenChannels: HidChannel[],
    sendChannel: HidChannel,
    payload: Uint8Array,
  ): Promise<DataView | 'disconnected'> {
    return this.exchangeUsbOtaInputReport(listenChannels, sendChannel, payload, {
      timeoutMs: COMMAND_RESPONSE_TIMEOUT_MS,
      accept: (reportId, data) => isTelinkMouseEnterUsbOtaSuccess(data, reportId),
      timeoutMessage:
        '设备未回进入升级模式成功包（04 AA BB CC 00 00 00 00 FF）。Report ID 05 回包与请求回显都不能当作成功',
    })
  }

  /**
   * 写 BIN 单帧：发 Output 后等任意可接受的 Input 作背压（对齐 C#）。
   * 在主控制口发送时，只接受 OTA Report ID，避免误吃 AP 控制包。
   */
  private async exchangeAnyUsbOtaInput(
    sendChannel: HidChannel,
    payload: Uint8Array,
  ): Promise<DataView | 'disconnected'> {
    return this.exchangeUsbOtaInputReport([sendChannel], sendChannel, payload, {
      timeoutMs: OTA_FRAME_TIMEOUT_MS,
      accept: (reportId) =>
        sendChannel !== this.device.channel || reportId === TELINK_MOUSE_USB_OTA_HID.reportId,
      timeoutMessage: '等待 Bootloader Input Report 超时（C# 以任意 Input 为背压）',
    })
  }

  /**
   * OTA 通用「多通道听 + 单通道发」交换：
   * 在 `listenChannels` 上挂同一 listener，向 `sendChannel` 发 Report ID `0x05`；
   * 超时且主通道已断则视为 `'disconnected'`，否则抛 TIMEOUT。
   */
  private async exchangeUsbOtaInputReport(
    listenChannels: HidChannel[],
    sendChannel: HidChannel,
    payload: Uint8Array,
    options: {
      timeoutMs: number
      accept: (reportId: number, data: DataView) => boolean
      timeoutMessage: string
    },
  ): Promise<DataView | 'disconnected'> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup()
        if (!this.device.channel.isConnected) {
          resolve('disconnected')
          return
        }
        reject(new DeviceError(DeviceErrorCode.TIMEOUT, options.timeoutMessage))
      }, options.timeoutMs)

      const listener: HidReportListener = (reportId, data) => {
        if (!options.accept(reportId, data)) return
        cleanup()
        resolve(data)
      }

      const cleanup = () => {
        clearTimeout(timer)
        for (const channel of listenChannels) {
          channel.offInputReport(listener)
        }
      }

      for (const channel of listenChannels) {
        channel.onInputReport(listener)
      }

      sendChannel.sendOutputReport(TELINK_MOUSE_USB_OTA_HID.reportId, payload).catch((error) => {
        cleanup()
        reject(error)
      })
    })
  }

  /**
   * 释放升级通道。
   * 若与主控制口共用同一个 native HID，只能 `unregister`，不能 `close`（否则主通道一并关掉）。
   */
  private async releaseUsbOtaChannel(channel: HidChannel): Promise<void> {
    const sharesPrimaryNative =
      Boolean(channel.nativeDevice) && channel.nativeDevice === this.device.channel.nativeDevice
    if (sharesPrimaryNative) {
      this.device.session.channels.unregister({ usageType: 'update' })
      return
    }
    await this.device.session.channels.close({ usageType: 'update' })
  }

  /** 写 BIN 进度（0–100）→ `FirmwareEvents.WritedData`。 */
  private reportProgress(progress: number): void {
    this.device.emit(FirmwareEvents.WritedData, {
      progress: Math.floor(Math.min(100, Math.max(0, progress))),
    })
  }

  /** 升级阶段文案 → `FirmwareEvents.Progress`（供 UI 阶段条）。 */
  private reportStage(stage: FirmwareUpdateStage, message: string, progress?: number): void {
    this.device.emit(FirmwareEvents.Progress, { stage, message, progress })
  }
}
